import { ApiError } from './api.js';

// Turning a refusal into something an attendee can act on.
//
// The backend answers a refused public request with
// `{ error: { message, details: { reason } } }` (design §3). The reason is the
// part worth branching on: "this link has expired" and "the organisers closed
// this form" are different situations with different next steps, and collapsing
// them into one apology is what generates the support email.

export const REFUSAL_REASONS = ['event_ended', 'form_closed', 'link_revoked'] as const;
export type RefusalReason = (typeof REFUSAL_REASONS)[number];

export type FailureKind = 'refused' | 'not_found' | 'offline' | 'error';

export interface Failure {
  kind: FailureKind;
  /** The machine-readable code, when the server sent one we know. */
  reason: RefusalReason | null;
  title: string;
  body: string;
  /** A second line only some states have — what to do next. */
  hint: string | null;
}

const isRefusalReason = (value: string | null): value is RefusalReason =>
  value !== null && (REFUSAL_REASONS as readonly string[]).includes(value);

const REFUSALS: Record<RefusalReason, Omit<Failure, 'kind' | 'reason'>> = {
  event_ended: {
    title: 'This event has finished',
    body: 'Forms close when the event ends, so this one is no longer accepting responses.',
    hint: 'Anything you submitted before it ended was saved.',
  },
  form_closed: {
    title: 'This form is closed',
    body: 'The organisers have stopped accepting responses to it.',
    hint: 'Anything you submitted while it was open was saved.',
  },
  link_revoked: {
    title: 'This link is no longer active',
    body: 'The organisers withdrew it, or replaced it with a newer one.',
    hint: 'If you were sent a more recent email, open the link in that one instead.',
  },
};

const NOT_FOUND: Record<'form' | 'site', Omit<Failure, 'kind' | 'reason'>> = {
  form: {
    title: "We couldn't find that form",
    body: 'The link may have been shortened, wrapped by an email client, or only partly copied.',
    hint: 'Open it straight from the email rather than pasting it, and check nothing is missing from the end.',
  },
  site: {
    title: "We couldn't find that event",
    body: 'It may not be published yet, or the address may be slightly off.',
    hint: 'Check the link the organisers sent you.',
  },
};

const OFFLINE: Omit<Failure, 'kind' | 'reason'> = {
  title: "We can't reach the event right now",
  body: 'Your device looks offline, or the connection dropped part-way.',
  hint: 'Check your connection and try again — nothing you typed has been sent.',
};

const UNKNOWN: Omit<Failure, 'kind' | 'reason'> = {
  title: 'Something went wrong at our end',
  body: 'This is not something you did, and trying again in a moment usually works.',
  hint: null,
};

// The statuses a refusal can arrive on. `BusinessRuleError` surfaces as 422 in
// this codebase, which is what the public form endpoints actually return; 410
// and 403 are here because the design table named 410 and a status is cheaper
// to accept than to argue with. None of them is what the decision turns on —
// see `explain` — they only decide the wording when the reason is unfamiliar.
const REFUSAL_STATUSES = new Set([403, 410, 422]);

/**
 * What went wrong, in words, for a given context.
 *
 * **The reason decides, not the status.** `details.reason` is the contract
 * (design §3); the HTTP code it rides on is an implementation detail of the
 * error class the backend happened to throw, and pinning behaviour to it is how
 * a page starts saying "something went wrong at our end" the day a
 * `BusinessRuleError` becomes a `ConflictError`.
 *
 * A refusal whose reason we do not recognise still reads as a refusal, in the
 * server's own words — a code added later degrades to a plain sentence, never
 * to a blank page and never to a raw status.
 */
export const explain = (error: unknown, context: 'form' | 'site'): Failure => {
  if (!(error instanceof ApiError)) return { kind: 'error', reason: null, ...UNKNOWN };

  if (isRefusalReason(error.reason)) {
    return { kind: 'refused', reason: error.reason, ...REFUSALS[error.reason] };
  }
  if (error.status === 0) return { kind: 'offline', reason: null, ...OFFLINE };
  if (error.status === 404) return { kind: 'not_found', reason: null, ...NOT_FOUND[context] };
  // A refusal status with no per-field errors is a refusal we have no wording
  // for. With field errors it is a validation failure, which belongs under the
  // fields rather than on a whole-page state — so it falls through.
  if (REFUSAL_STATUSES.has(error.status) && Object.keys(error.fields).length === 0) {
    return {
      kind: 'refused',
      reason: null,
      title: 'This is no longer available',
      body: error.message || 'The organisers have closed it.',
      hint: null,
    };
  }
  if (error.status === 429) {
    return {
      kind: 'error',
      reason: null,
      title: 'Too many attempts',
      body: 'You have opened this a lot in a short time, so we have paused it briefly.',
      hint: 'Wait a minute, then reload.',
    };
  }
  if (error.status === 413) {
    // The 64 KB body cap is a Fastify per-route bodyLimit, so this comes back
    // *before* the answers are parsed — there is no `details.fields` to hang
    // it on, and the generic apology would be a lie. It is nearly always one
    // long-text field pasted from a document.
    return {
      kind: 'error',
      reason: null,
      title: 'That is more than we can accept',
      body: 'One of your answers is too long to send.',
      hint: 'Shorten your longest answer and try again — nothing has been saved.',
    };
  }
  return { kind: 'error', reason: null, ...UNKNOWN };
};

/** Whether retrying could plausibly help — drives whether a Try again button shows. */
export const isRetryable = (failure: Failure): boolean =>
  failure.kind === 'offline' || failure.kind === 'error';
